import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ProfileInfoPanel } from './profile/ProfileInfoPanel';
import { ChangePasswordPanel } from './profile/ChangePasswordPanel';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

/**
 * Profile Modal - User profile and security settings
 *
 * Provides access to:
 * - Basic Information (name, job title, phone, timezone)
 * - Security (password change)
 *
 * Uses InfoPanel components for consistent UI with edit/view modes.
 */
export default function ProfileModal({ isOpen, onClose, onUpdate }: ProfileModalProps) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleUpdate = () => {
    onUpdate?.();
  };

  const modalContent = (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={-1}
        className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-3xl bg-white dark:bg-zinc-900 rounded-xl shadow-2xl pointer-events-auto">
          {/* Header with close button */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-800">
            <div>
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-white">
                Profile Settings
              </h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Manage your personal information and security
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Panels */}
          <div className="p-6 max-h-[calc(100vh-12rem)] overflow-y-auto space-y-6">
            <ProfileInfoPanel onUpdate={handleUpdate} />
            <ChangePasswordPanel onUpdate={handleUpdate} />
          </div>
        </div>
      </div>
    </>
  );

  // Render modal at document.body level to avoid sidebar constraints
  return createPortal(modalContent, document.body);
}
